<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="FAQs"
      subtitle="Manage frequently asked questions shown to society members."
      :headers="headers"
      :items="faqs"
      :loading="loading"
      show-search
      search-placeholder="Search FAQs..."
      show-add
      add-label="Add FAQ"
      @add="openAdd"
    >
      <!-- Question -->
      <template v-slot:item.question="{ item }">
        <TruncatedCell :text="item.question" max-width="260px" />
      </template>

      <!-- Answer -->
      <template v-slot:item.answer="{ item }">
        <TruncatedCell :text="item.answer" max-width="300px" />
      </template>

      <!-- Date -->
      <template v-slot:item.created_at="{ item }">
        <span class="ism-date-text">{{ formatDate(item.created_at) }}</span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-pencil-outline"
            label="Edit"
            @click="openEdit(item)"
          />
          <PremiumActionButton
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="confirmDelete(item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete FAQ"
      message="Are you sure you want to delete this FAQ?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteFaq"
      @cancel="deleteDialog = false"
    />

    <!-- Add/Edit Modal -->
    <PremiumModalForm
      v-model="faqDialog"
      :title="isEdit ? 'Edit FAQ' : 'Add FAQ'"
      :submitText="isEdit ? 'Update FAQ' : 'Save FAQ'"
      :loading="saving"
      @submit="saveFaq"
    >
      <div style="display:flex;flex-direction:column;gap:10px;">
        <PremiumInput
          v-model="faq.question"
          name="question"
          label="Question *"
          placeholder="Enter question"
          is-textarea
          rows="2"
          v-validate="'required'"
          :error="errors.first('question') || serverSideErrors.question"
        />
        <PremiumInput
          v-model="faq.answer"
          name="answer"
          label="Answer *"
          placeholder="Enter answer"
          is-textarea
          rows="4"
          v-validate="'required'"
          :error="errors.first('answer') || serverSideErrors.answer"
        />
      </div>
    </PremiumModalForm>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import PremiumTable from '../../common/PremiumTable.vue';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PremiumActionButton from '../../common/PremiumActionButton.vue';
import PremiumModalForm from '../../common/PremiumModalForm.vue';
import PremiumInput from '../../common/PremiumInput.vue';
import TruncatedCell from '../../common/TruncatedCell.vue';

export default {
  name: 'FaqList',
  components: { PremiumTable, ConfirmModal, PremiumActionButton, PremiumModalForm, PremiumInput, TruncatedCell },

  data() {
    return {
      faqs:          [],
      loading:       false,
      saving:        false,
      deleteDialog:  false,
      faqDialog:     false,
      isEdit:        false,
      deletingId:    null,
      editId:        null,
      faq: {
        question: '',
        answer:   '',
      },
      serverSideErrors: {},

      headers: [
        { text: 'Question', value: 'question',   width: '38%' },
        { text: 'Answer',   value: 'answer',     width: '42%' },
        { text: 'Date',     value: 'created_at', width: '12%' },
        { text: 'Actions',  value: 'actions',    width: '8%',  sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    formatDate(d) { return moment(d).format('DD MMM, YYYY'); },

    getFaqs() {
      this.loading = true;
      this.$axios.get('superadmin/faqs').then(res => {
        this.loading = false;
        if (res.data.status) this.faqs = res.data.faqs;
        else this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
      }).catch(() => { this.loading = false; });
    },

    confirmDelete(id) {
      this.deletingId  = id;
      this.deleteDialog = true;
    },

    deleteFaq() {
      this.deleteDialog = false;
      this.$axios.delete(`superadmin/faqs/${this.deletingId}`).then(res => {
        if (res.data.status) {
          this.getFaqs();
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      });
    },

    openAdd() {
      this.isEdit = false;
      this.faq = { question: '', answer: '' };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.faqDialog = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.editId = item.id;
      this.faq = JSON.parse(JSON.stringify(item));
      this.serverSideErrors = {};
      this.$validator.reset();
      this.faqDialog = true;
    },

    saveFaq() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        const method = this.isEdit ? 'put' : 'post';
        const url    = this.isEdit ? `superadmin/faqs/${this.editId}` : 'superadmin/faqs';

        this.$axios[method](url, this.faq).then(res => {
          this.saving = false;
          if (res.data.status) {
            this.faqDialog = false;
            this.getFaqs();
            this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
          } else {
            if (res.data.errors) {
              res.data.errors.forEach(err => { this.serverSideErrors[err.field] = err.message; });
            } else {
              this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
            }
          }
        }).catch(() => { this.saving = false; });
      });
    },
  },

  mounted() {
    this.getFaqs();
  },
};
</script>

<style scoped>
.ism-text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-primary, #1e293b);
}
.ism-text-secondary {
  color: var(--text-secondary, #64748b) !important;
}
.ism-date-text {
  font-size: 12.5px;
  color: var(--text-secondary, #64748b);
  white-space: nowrap;
}
</style>
